import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Link2, MoreHorizontal, Plus, Trash2, Unlink } from 'lucide-react';
import Toast from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import Nr2jTaskDrawer from '../../components/nr2j/Nr2jTaskDrawer';
import Nr2jPlanFollowUpModal from '../../components/nr2j/Nr2jPlanFollowUpModal';
import apiService from '../../services/api';
import { buildCommercantPublicUrl } from '../../components/prestation-wizard/buildWizardSiteUrls';
import {
  computeConversionMissing,
  effectiveStatus,
  effectiveContractStatus,
  formatCentsAsEuros,
  formatNr2jDate,
  formatNr2jDateOnly,
  isNr2jOrgFieldSatisfied,
  isQuoteFullyDraftDeletable,
  mapConversionMissingToOrgFields,
  NR2J_CONVERSION_MISSING_LABELS,
  NR2J_CONVERSION_TO_ORG_FIELDS,
  NR2J_PIPELINE_STAGES,
  NR2J_QUOTE_SEND_GATE_LABELS,
  NR2J_TASK_TYPES,
  nr2jContractListBadge,
  nr2jInvoiceDisplayBadge,
  nr2jQuoteStatusBadge,
  nr2jStatusBadge,
  nr2jTaskStatusLabel,
  nr2jTaskTypeLabel,
  organizationDisplayName,
  contactDisplayName,
} from '../../utils/nr2jConstants';

const emptyContact = {
  prenom: '',
  nom: '',
  fonction: '',
  email: '',
  telephone: '',
  isPrimary: false,
  isBillingContact: false,
  isLegalRepresentative: false,
};

const MISSING_LABELS = NR2J_CONVERSION_MISSING_LABELS;

const HIGHLIGHT_HINTS = {
  quote: 'Requis pour envoyer le devis',
  convert: 'Requis pour convertir en client NR2J',
  invoice: 'Requis pour créer la facture',
};

const IDENTITY_FIELDS = [
  ['Raison sociale', 'raisonSociale'],
  ['Nom commercial', 'nomCommercial'],
  ['Forme juridique', 'formeJuridique'],
];
const LEGAL_FIELDS = [
  ['SIREN', 'siren'],
  ['SIRET', 'siret'],
  ['TVA intracommunautaire', 'tvaIntracommunautaire'],
];
const CONTACT_FIELDS = [
  ['Email principal', 'emailPrincipal'],
  ['Email facturation', 'emailFacturation'],
  ['Téléphone', 'telephone'],
  ['Site web', 'siteWeb'],
];
const ADDRESS_FIELDS = [
  ['Adresse', 'adresse'],
  ['Complément', 'complementAdresse'],
  ['Code postal', 'codePostal'],
  ['Ville', 'ville'],
  ['Pays', 'pays'],
];

function FieldGrid({
  fields,
  org,
  onBlurSave,
  highlightKeys = [],
  requiredHint = HIGHLIGHT_HINTS.quote,
  onLiveChange,
}) {
  const highlight = new Set(highlightKeys || []);
  const [draft, setDraft] = useState({});
  useEffect(() => {
    setDraft({});
  }, [org?.id, org?.updatedAt]);

  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {fields.map(([label, key]) => {
        const value = draft[key] !== undefined ? draft[key] : (org[key] || '');
        const wasHighlighted = highlight.has(key);
        const draftOrg = { ...org, ...draft, [key]: value };
        const satisfied = isNr2jOrgFieldSatisfied(key, value, draftOrg);
        const isMissing = wasHighlighted && !satisfied;
        const isValidated = wasHighlighted && satisfied;
        return (
          <label
            key={key}
            data-field={key}
            data-field-state={isMissing ? 'missing' : isValidated ? 'valid' : 'neutral'}
            className={`block text-sm ${
              isMissing
                ? 'rounded-lg ring-2 ring-rose-300 ring-offset-2'
                : isValidated
                  ? 'rounded-lg ring-2 ring-emerald-300 ring-offset-2'
                  : ''
            }`}
          >
            <span className={
              isMissing
                ? 'font-semibold text-rose-700'
                : isValidated
                  ? 'font-semibold text-emerald-700'
                  : 'text-gray-500'
            }
            >
              {label}
              {isMissing ? ` — ${requiredHint}` : ''}
              {isValidated ? ' — OK' : ''}
            </span>
            <input
              className={`mt-1 w-full rounded-lg border px-3 py-2 ${
                isMissing
                  ? 'border-rose-500 bg-rose-50 focus:border-rose-600 focus:ring-rose-200'
                  : isValidated
                    ? 'border-emerald-500 bg-emerald-50 focus:border-emerald-600 focus:ring-emerald-200'
                    : 'border-gray-300'
              }`}
              value={value}
              onChange={(e) => {
                const next = e.target.value;
                const nextDraft = { ...draft, [key]: next };
                setDraft(nextDraft);
                onLiveChange?.(key, next, { ...org, ...nextDraft });
              }}
              onBlur={(e) => {
                const next = e.target.value;
                if ((org[key] || '') !== next) onBlurSave({ [key]: next || null });
              }}
            />
            {isMissing ? (
              <span className="mt-1 block text-xs font-medium text-rose-600">{requiredHint}</span>
            ) : null}
            {isValidated ? (
              <span className="mt-1 block text-xs font-medium text-emerald-700">Champ valide</span>
            ) : null}
          </label>
        );
      })}
    </div>
  );
}

export default function Nr2jOrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toasts, toast, removeToast } = useToast();

  const urlMissingKeys = useMemo(
    () => String(searchParams.get('missing') || '').split(',').map((s) => s.trim()).filter(Boolean),
    [searchParams],
  );
  const ficheIntent = useMemo(() => {
    const raw = String(searchParams.get('intent') || '').trim();
    if (raw === 'invoice' || raw === 'convert' || raw === 'quote') return raw;
    // Heuristique : clés conversion (siren…) vs gates devis
    const looksConversion = urlMissingKeys.some((k) => Boolean(NR2J_CONVERSION_TO_ORG_FIELDS[k]));
    return looksConversion ? 'convert' : 'quote';
  }, [searchParams, urlMissingKeys]);
  const returnToQuote = useMemo(() => {
    const raw = searchParams.get('returnTo') || '';
    if (!raw.startsWith('/nr2j/devis/')) return null;
    return raw;
  }, [searchParams]);

  // États déclarés AVANT les useMemo qui les lisent (évite TDZ → crash minifié « can't access lexical declaration 'A' »).
  const [localHighlightMissing, setLocalHighlightMissing] = useState([]);
  const [liveDraftOrg, setLiveDraftOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [activities, setActivities] = useState([]);
  const [commercants, setCommercants] = useState([]);
  const [linkCommercantId, setLinkCommercantId] = useState('');
  const [saving, setSaving] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [contactDeleteBusy, setContactDeleteBusy] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    type: 'APPEL',
    dueAt: '',
    notes: '',
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertMissing, setConvertMissing] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpConflict, setFollowUpConflict] = useState(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [contractBusy, setContractBusy] = useState(false);

  const activeMissingKeys = useMemo(() => {
    const merged = [...new Set([...urlMissingKeys, ...localHighlightMissing])];
    // Facturation : contact principal non bloquant si email société valide.
    if (ficheIntent !== 'invoice') return merged;
    if (!org) return merged.filter((k) => k !== 'primaryContact');
    const draft = liveDraftOrg || org;
    const still = computeConversionMissing(draft, draft.contacts || org.contacts || [], { forInvoice: true });
    return merged.filter((k) => still.includes(k));
  }, [urlMissingKeys, localHighlightMissing, ficheIntent, org, liveDraftOrg]);
  const highlightIntent = useMemo(() => {
    if (localHighlightMissing.length) return ficheIntent === 'invoice' ? 'invoice' : 'convert';
    return ficheIntent;
  }, [localHighlightMissing, ficheIntent]);
  const highlightFieldKeys = useMemo(
    () => mapConversionMissingToOrgFields(activeMissingKeys),
    [activeMissingKeys],
  );

  const handleFieldLiveChange = useCallback((_key, _value, draftOrg) => {
    setLiveDraftOrg(draftOrg);
  }, []);

  const bannerMissingKeysLive = useMemo(() => {
    if (!org) return activeMissingKeys;
    const draft = liveDraftOrg || org;
    const forInvoice = highlightIntent === 'invoice';
    const still = computeConversionMissing(draft, draft.contacts || org.contacts || [], { forInvoice });
    return activeMissingKeys.filter((k) => still.includes(k));
  }, [org, liveDraftOrg, activeMissingKeys, highlightIntent]);
  const requiredHint = HIGHLIGHT_HINTS[highlightIntent] || HIGHLIGHT_HINTS.convert;
  const bannerMissingKeys = bannerMissingKeysLive;
  const showContextBanner = bannerMissingKeys.length > 0 || Boolean(returnToQuote) || localHighlightMissing.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, acts, shops, quotesRes, invoicesRes, contractsRes] = await Promise.all([
        apiService.getNr2jOrganization(id),
        apiService.listNr2jActivities(id),
        apiService.getCommercants().catch(() => ({ commercants: [] })),
        apiService.listNr2jQuotes({ organizationId: id }).catch(() => ({ quotes: [] })),
        apiService.listNr2jInvoices({ organizationId: id }).catch(() => ({ invoices: [] })),
        apiService.listNr2jContracts({ organizationId: id }).catch(() => ({ contracts: [] })),
      ]);
      setOrg(detail?.organization || detail);
      setActivities(acts?.activities || []);
      setQuotes(quotesRes?.quotes || []);
      setInvoices(invoicesRes?.invoices || []);
      setContracts(contractsRes?.contracts || []);
      const list = shops?.commercants || shops?.data || shops || [];
      setCommercants(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(error?.message || 'Société introuvable');
      navigate('/nr2j/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load, location.key]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') load();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // Autofocus / scroll : UNE SEULE fois à l’entrée du workflow de complétion
  // (ou après clic « Compléter la fiche » via missingSessionKey).
  // Ne PAS dépendre de highlightFieldKeys / liveDraft — sinon chaque frappe
  // (rouge→vert) re-scroll/focus le premier manquant et vole le curseur.
  const initialMissingFocusDoneRef = useRef(false);
  const missingSessionKey = `${id}|${searchParams.get('intent') || ''}|${searchParams.get('missing') || ''}|${returnToQuote || ''}|${localHighlightMissing.join(',')}`;
  useEffect(() => {
    initialMissingFocusDoneRef.current = false;
  }, [missingSessionKey]);

  useEffect(() => {
    if (loading || !org) return undefined;
    if (initialMissingFocusDoneRef.current) return undefined;
    // Snapshot figé au moment de l’entrée — pas la liste live.
    const keysAtEntry = highlightFieldKeys;
    if (!keysAtEntry.length) return undefined;
    initialMissingFocusDoneRef.current = true;
    const firstKey = keysAtEntry[0];
    const t = window.setTimeout(() => {
      // Ne pas voler le focus si l’utilisateur a déjà cliqué dans un autre champ.
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')) {
        return;
      }
      const el = document.querySelector(`[data-field="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = el.querySelector('input, textarea, select');
        if (input) input.focus({ preventScroll: true });
      }
    }, 120);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- volontairement hors deps highlightFieldKeys
  }, [loading, org, missingSessionKey]);

  const badge = useMemo(() => nr2jStatusBadge(org?.commercialStatus), [org?.commercialStatus]);
  const linkedIds = useMemo(
    () => new Set((org?.commercantLinks || []).map((l) => l.commercantId)),
    [org],
  );
  const linkableShops = useMemo(
    () => commercants.filter((c) => c?.id && !linkedIds.has(c.id)),
    [commercants, linkedIds],
  );

  async function patchOrg(patch) {
    setSaving(true);
    try {
      const res = await apiService.updateNr2jOrganization(id, patch);
      setOrg(res?.organization || res);
      toast.success('Enregistré');
      const acts = await apiService.listNr2jActivities(id);
      setActivities(acts?.activities || []);
    } catch (error) {
      toast.error(error?.message || 'Échec de la mise à jour');
    } finally {
      setSaving(false);
    }
  }

  async function addContact(e) {
    e.preventDefault();
    try {
      if (editingContact) {
        await apiService.updateNr2jContact(editingContact.id, contactForm);
        toast.success('Contact mis à jour');
      } else {
        await apiService.createNr2jContact(id, contactForm);
        toast.success('Contact ajouté');
      }
      setContactForm(emptyContact);
      setEditingContact(null);
      load();
    } catch (error) {
      toast.error(error?.message || 'Impossible d’enregistrer le contact');
    }
  }

  async function confirmDeleteContact() {
    if (!contactToDelete?.id) return;
    setContactDeleteBusy(true);
    try {
      await apiService.deleteNr2jContact(contactToDelete.id);
      toast.success('Contact supprimé');
      if (editingContact?.id === contactToDelete.id) {
        setEditingContact(null);
        setContactForm(emptyContact);
      }
      setContactToDelete(null);
      load();
    } catch (error) {
      toast.error(error?.message || 'Impossible de supprimer le contact');
    } finally {
      setContactDeleteBusy(false);
    }
  }

  async function addTask(e) {
    e.preventDefault();
    try {
      await apiService.createNr2jTask(id, {
        title: taskForm.title,
        type: taskForm.type,
        dueAt: taskForm.dueAt ? new Date(taskForm.dueAt).toISOString() : null,
        notes: taskForm.notes || null,
      });
      setTaskForm({ title: '', type: 'APPEL', dueAt: '', notes: '' });
      toast.success('Tâche créée');
      load();
    } catch (error) {
      toast.error(error?.message || 'Impossible de créer la tâche');
    }
  }

  async function tryConvert() {
    setSaving(true);
    try {
      const res = await apiService.convertNr2jOrganizationToClient(id);
      setOrg(res?.organization || res);
      setConvertOpen(false);
      setConvertMissing([]);
      setLocalHighlightMissing([]);
      toast.success('Converti en client NR2J');
      load();
    } catch (error) {
      const missing =
        error?.data?.error?.missing
        || error?.response?.data?.error?.missing
        || error?.missing
        || [];
      if (Array.isArray(missing) && missing.length) {
        setConvertMissing(missing);
        setConvertOpen(true);
      } else {
        toast.error(error?.message || 'Conversion impossible');
      }
    } finally {
      setSaving(false);
    }
  }

  async function archiveOrg() {
    if (!window.confirm('Archiver cette fiche ? Elle quittera les listes actives. L’historique est conservé.')) {
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.archiveNr2jOrganization(id);
      setOrg(res?.organization || res);
      toast.success('Fiche archivée');
      load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setSaving(false);
      setMenuOpen(false);
    }
  }

  async function confirmDeleteOrg() {
    setSaving(true);
    try {
      await apiService.deleteNr2jOrganization(id);
      toast.success('Prospect supprimé définitivement');
      navigate('/prospection');
      return true;
    } catch (error) {
      toast.error(error?.message || 'Échec de la suppression');
      return false;
    } finally {
      setSaving(false);
      setMenuOpen(false);
    }
  }

  async function reactivateOrg() {
    setSaving(true);
    try {
      const res = await apiService.reactivateNr2jOrganization(id);
      setOrg(res?.organization || res);
      toast.success('Fiche réactivée comme prospect');
      load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    } finally {
      setSaving(false);
      setMenuOpen(false);
    }
  }

  async function linkShop(e) {
    e.preventDefault();
    if (!linkCommercantId) return;
    try {
      await apiService.linkNr2jCommercant(id, linkCommercantId);
      setLinkCommercantId('');
      toast.success('Boutique liée');
      load();
    } catch (error) {
      toast.error(error?.message || 'Liaison impossible');
    }
  }

  async function unlinkShop(commercantId) {
    if (!window.confirm('Délier cette boutique Clickorico ?')) return;
    try {
      await apiService.unlinkNr2jCommercant(id, commercantId);
      toast.success('Boutique déliée');
      load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    }
  }

  if (loading || !org) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="nr2j-org-loading">
        Chargement de la société…
      </div>
    );
  }

  const backHref = org.commercialStatus === 'CLIENT'
    ? '/nr2j/clients'
    : org.commercialStatus === 'INACTIVE'
      ? '/prospection?filter=archived'
      : '/prospection';

  const checklistKeys = ['identity', 'siren', 'siret', 'email', 'adresse', 'codePostal', 'ville', 'pays', 'primaryContact'];

  return (
    <div className="space-y-6" data-testid="nr2j-org-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-indigo-700">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{organizationDisplayName(org)}</h1>
            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            {org.ville || 'Ville non renseignée'}
            {org.emailPrincipal ? ` · ${org.emailPrincipal}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {org.commercialStatus === 'PROSPECT' ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                const missing = computeConversionMissing(org, org.contacts || []);
                setConvertMissing(missing);
                setConvertOpen(true);
              }}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white"
            >
              Convertir en client NR2J
            </button>
          ) : null}
          {org.commercialStatus === 'PROSPECT' || org.commercialStatus === 'CLIENT' ? (
            <>
              <Link
                to={`/nr2j/devis/nouveau?organizationId=${id}`}
                className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-900"
              >
                <FileText className="h-4 w-4" /> Créer un devis
              </Link>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setFollowUpConflict(null);
                  setFollowUpOpen(true);
                }}
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900"
              >
                Planifier une relance
              </button>
            </>
          ) : null}
          <select
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={org.pipelineStage || 'NOUVEAU'}
            disabled={saving || org.commercialStatus !== 'PROSPECT'}
            onChange={(e) => patchOrg({ pipelineStage: e.target.value })}
          >
            {NR2J_PIPELINE_STAGES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-lg border border-gray-300 p-2"
              aria-label="Actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen ? (
              <div className="absolute right-0 z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {org.commercialStatus === 'INACTIVE' ? (
                  <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50" onClick={reactivateOrg}>
                    Réactiver
                  </button>
                ) : (
                  <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50" onClick={archiveOrg}>
                    Archiver la fiche
                  </button>
                )}
                {org.commercialStatus === 'PROSPECT' ? (
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                    onClick={() => {
                      setMenuOpen(false);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    Supprimer définitivement
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showContextBanner ? (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
          data-testid="nr2j-org-context-banner"
        >
          <p className="font-semibold">
            {highlightIntent === 'invoice'
              ? 'Complétez les informations suivantes pour créer la facture :'
              : highlightIntent === 'convert'
                ? 'Complétez les informations suivantes pour convertir cette société en client NR2J :'
                : 'Complétez les informations suivantes pour pouvoir envoyer le devis :'}
          </p>
          {bannerMissingKeys.length > 0 ? (
            <ul className="mt-2 list-inside list-disc">
              {bannerMissingKeys.map((key) => (
                <li key={key}>
                  {NR2J_CONVERSION_MISSING_LABELS[key]
                    || NR2J_QUOTE_SEND_GATE_LABELS[key]
                    || key}
                </li>
              ))}
            </ul>
          ) : null}
          {returnToQuote ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={returnToQuote}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                data-testid="nr2j-org-return-to-quote"
              >
                Retour au devis
              </Link>
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  await load();
                  navigate(returnToQuote);
                }}
                className="inline-flex items-center rounded-lg border border-indigo-300 bg-white px-3 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-50"
                data-testid="nr2j-org-save-return-quote"
              >
                Enregistrer et revenir au devis
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-3">
        <section className="space-y-6 xl:col-span-2">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">1. Identité</h2>
            <FieldGrid
              key={`identity-${org.id}-${org.updatedAt || ''}`}
              fields={IDENTITY_FIELDS}
              org={org}
              onBlurSave={patchOrg}
              onLiveChange={handleFieldLiveChange}
              highlightKeys={highlightFieldKeys}
              requiredHint={requiredHint}
            />
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">2. Informations légales</h2>
            <FieldGrid
              key={`legal-${org.id}-${org.updatedAt || ''}`}
              fields={LEGAL_FIELDS}
              org={org}
              onBlurSave={patchOrg}
              onLiveChange={handleFieldLiveChange}
              highlightKeys={highlightFieldKeys}
              requiredHint={requiredHint}
            />
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">3. Coordonnées</h2>
            <FieldGrid
              key={`coords-${org.id}-${org.updatedAt || ''}`}
              fields={[...CONTACT_FIELDS, ...ADDRESS_FIELDS]}
              org={org}
              onBlurSave={patchOrg}
              onLiveChange={handleFieldLiveChange}
              highlightKeys={highlightFieldKeys}
              requiredHint={requiredHint}
            />
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">4. Suivi commercial</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-gray-500">Offre envisagée</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  defaultValue={org.interestOffer || ''}
                  onBlur={(e) => {
                    if ((org.interestOffer || '') !== e.target.value) {
                      patchOrg({ interestOffer: e.target.value || null });
                    }
                  }}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-500">Prochaine action</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  defaultValue={org.nextAction || ''}
                  onBlur={(e) => {
                    if ((org.nextAction || '') !== e.target.value) {
                      patchOrg({ nextAction: e.target.value || null });
                    }
                  }}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-500">Prochaine relance</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  defaultValue={
                    org.nextFollowUpAt
                      ? new Date(org.nextFollowUpAt).toISOString().slice(0, 16)
                      : ''
                  }
                  onBlur={(e) => {
                    const v = e.target.value;
                    patchOrg({ nextFollowUpAt: v ? new Date(v).toISOString() : null });
                  }}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-gray-500">Notes commerciales</span>
                <textarea
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  rows={3}
                  defaultValue={org.commercialNotes || ''}
                  onBlur={(e) => {
                    if ((org.commercialNotes || '') !== e.target.value) {
                      patchOrg({ commercialNotes: e.target.value || null });
                    }
                  }}
                />
              </label>
            </div>
          </div>

          <div
            className={`rounded-xl border bg-white p-5 shadow-sm ${
              highlightFieldKeys.includes('primaryContact')
                ? 'border-rose-400 ring-2 ring-rose-300 ring-offset-2'
                : 'border-gray-200'
            }`}
            data-field="primaryContact"
          >
            <h2 className={`text-sm font-semibold uppercase tracking-wide ${
              highlightFieldKeys.includes('primaryContact') ? 'text-rose-700' : 'text-gray-500'
            }`}
            >
              5. Contacts
              {highlightFieldKeys.includes('primaryContact') ? ` — ${requiredHint}` : ''}
            </h2>
            {highlightFieldKeys.includes('primaryContact') ? (
              <p className="mt-2 text-xs font-medium text-rose-600">
                Contact principal requis (prénom, nom, email valide).
              </p>
            ) : null}
            <ul className="mt-3 divide-y divide-gray-100">
              {(org.contacts || []).map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-2 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-900">
                      {contactDisplayName(c) || 'Contact'}
                      {c.isPrimary ? <span className="ml-2 text-xs text-indigo-700">Principal</span> : null}
                    </p>
                    <p className="text-xs text-gray-500">
                      {[c.fonction, c.email, c.telephone].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="text-xs text-indigo-700"
                      onClick={() => {
                        setEditingContact(c);
                        setContactForm({
                          prenom: c.prenom || '',
                          nom: c.nom || '',
                          fonction: c.fonction || '',
                          email: c.email || '',
                          telephone: c.telephone || '',
                          isPrimary: Boolean(c.isPrimary),
                          isBillingContact: Boolean(c.isBillingContact),
                          isLegalRepresentative: Boolean(c.isLegalRepresentative),
                        });
                      }}
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900"
                      title="Supprimer le contact"
                      aria-label={`Supprimer ${contactDisplayName(c) || 'contact'}`}
                      data-testid="nr2j-contact-delete"
                      onClick={() => setContactToDelete(c)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </button>
                  </div>
                </li>
              ))}
              {(org.contacts || []).length === 0 ? (
                <li className="py-2 text-sm text-gray-500">Aucun contact.</li>
              ) : null}
            </ul>
            <form
              onSubmit={addContact}
              className={`mt-4 grid gap-2 sm:grid-cols-2 ${
                highlightFieldKeys.includes('primaryContact')
                  ? 'rounded-lg border border-rose-400 bg-rose-50/60 p-3'
                  : ''
              }`}
            >
              <p className="sm:col-span-2 text-xs font-medium text-gray-600">
                {editingContact ? 'Modifier le contact' : 'Ajouter un contact'}
              </p>
              <input required placeholder="Prénom" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={contactForm.prenom} onChange={(e) => setContactForm((f) => ({ ...f, prenom: e.target.value }))} />
              <input required placeholder="Nom" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={contactForm.nom} onChange={(e) => setContactForm((f) => ({ ...f, nom: e.target.value }))} />
              <input placeholder="Fonction" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={contactForm.fonction} onChange={(e) => setContactForm((f) => ({ ...f, fonction: e.target.value }))} />
              <input placeholder="Email" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
              <input placeholder="Téléphone" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={contactForm.telephone} onChange={(e) => setContactForm((f) => ({ ...f, telephone: e.target.value }))} />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={contactForm.isPrimary} onChange={(e) => setContactForm((f) => ({ ...f, isPrimary: e.target.checked }))} />
                Contact principal
              </label>
              <div className="flex gap-2 sm:col-span-2">
                <button type="submit" className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white">
                  <Plus className="h-4 w-4" /> {editingContact ? 'Enregistrer' : 'Ajouter le contact'}
                </button>
                {editingContact ? (
                  <button type="button" className="rounded-lg border px-3 py-2 text-sm" onClick={() => { setEditingContact(null); setContactForm(emptyContact); }}>
                    Annuler
                  </button>
                ) : null}
              </div>
            </form>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-org-quotes">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">6. Devis</h2>
              <Link
                to={`/nr2j/devis/nouveau?organizationId=${id}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
              >
                <Plus className="h-3.5 w-3.5" /> Créer un devis
              </Link>
            </div>
            {quotes.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Aucun devis pour cette société.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100" data-testid="nr2j-org-quotes-list">
                {quotes.map((quote) => {
                  const v = quote.currentVersion;
                  const eff = v ? (v.effectiveStatus || effectiveStatus(v)) : null;
                  const badge = nr2jQuoteStatusBadge(eff);
                  const versionCount = quote.versionCount ?? quote.versions?.length ?? 0;
                  const deletable = isQuoteFullyDraftDeletable(quote);
                  return (
                    <li key={quote.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{quote.number}</p>
                        <p className="text-xs text-gray-600">
                          Version actuelle : v{v?.version ?? '—'}
                          {versionCount > 1 ? ` · ${versionCount} versions` : ''}
                        </p>
                        <p className="mt-0.5 text-sm text-gray-700">
                          {formatCentsAsEuros(v?.totalTtcCents)} €
                          {' · '}
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
                            {badge.label}
                          </span>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/nr2j/devis/${quote.id}`}
                          className="text-xs text-indigo-700 hover:underline"
                        >
                          Ouvrir
                        </Link>
                        {v?.hasPdf || v?.status === 'SENT' || v?.status === 'ACCEPTED' ? (
                          <button
                            type="button"
                            disabled={quoteBusy}
                            className="text-xs text-gray-600 hover:underline"
                            onClick={async () => {
                              setQuoteBusy(true);
                              try {
                                await apiService.downloadNr2jQuotePdf(quote.id, v.id, {
                                  number: quote.number,
                                  version: v.version,
                                });
                              } catch (error) {
                                toast.error(error?.message || 'PDF indisponible');
                              } finally {
                                setQuoteBusy(false);
                              }
                            }}
                          >
                            PDF
                          </button>
                        ) : null}
                        {eff === 'SENT' || eff === 'EXPIRED' || v?.status === 'REFUSED' ? (
                          <button
                            type="button"
                            disabled={quoteBusy}
                            className="text-xs text-amber-800 hover:underline"
                            onClick={async () => {
                              if (!window.confirm('Créer une nouvelle version brouillon ?')) return;
                              setQuoteBusy(true);
                              try {
                                const res = await apiService.newNr2jQuoteVersion(quote.id);
                                const updated = res?.quote || res;
                                toast.success('Nouvelle version créée');
                                navigate(`/nr2j/devis/${updated.id}`);
                              } catch (error) {
                                toast.error(error?.message || 'Échec');
                              } finally {
                                setQuoteBusy(false);
                              }
                            }}
                          >
                            Nouvelle version
                          </button>
                        ) : null}
                        {deletable ? (
                          <button
                            type="button"
                            disabled={quoteBusy}
                            className="inline-flex items-center gap-0.5 text-xs text-rose-700 hover:underline"
                            onClick={async () => {
                              if (!window.confirm(
                                'Supprimer définitivement ce brouillon ?\nCette action ne supprimera aucun devis déjà envoyé.',
                              )) return;
                              setQuoteBusy(true);
                              try {
                                await apiService.deleteNr2jQuote(quote.id);
                                toast.success('Brouillon supprimé');
                                load();
                              } catch (error) {
                                toast.error(error?.message || 'Suppression impossible');
                              } finally {
                                setQuoteBusy(false);
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" /> Supprimer
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-org-invoices">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">7. Factures</h2>
              <Link
                to={`/nr2j/facturation/nouveau?organizationId=${id}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
              >
                <Plus className="h-3.5 w-3.5" /> Nouvelle facture
              </Link>
            </div>
            {invoices.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Aucune facture pour cette société.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100" data-testid="nr2j-org-invoices-list">
                {invoices.map((invoice) => {
                  const invBadge = nr2jInvoiceDisplayBadge(invoice);
                  return (
                    <li key={invoice.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.number || 'Brouillon'}</p>
                        <p className="mt-0.5 text-sm text-gray-700">
                          {formatCentsAsEuros(invoice.totalTtcCents)} €
                          {' · solde '}
                          {formatCentsAsEuros(invoice.balanceCents)} €
                          {' · '}
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${invBadge.className}`}>
                            {invBadge.label}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">
                          Échéance {formatNr2jDateOnly(invoice.dueAt)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/nr2j/facturation/${invoice.id}`}
                          className="text-xs text-indigo-700 hover:underline"
                        >
                          Ouvrir
                        </Link>
                        <button
                          type="button"
                          disabled={invoiceBusy}
                          className="text-xs text-gray-600 hover:underline"
                          onClick={async () => {
                            setInvoiceBusy(true);
                            try {
                              await apiService.downloadNr2jInvoicePdf(invoice.id, {
                                number: invoice.number,
                              });
                            } catch (error) {
                              toast.error(error?.message || 'PDF indisponible');
                            } finally {
                              setInvoiceBusy(false);
                            }
                          }}
                        >
                          PDF
                        </button>
                        {invoice.status === 'DRAFT' ? (
                          <button
                            type="button"
                            disabled={invoiceBusy}
                            className="inline-flex items-center gap-0.5 text-xs text-rose-700 hover:underline"
                            onClick={async () => {
                              if (!window.confirm('Supprimer définitivement ce brouillon de facture ?')) return;
                              setInvoiceBusy(true);
                              try {
                                await apiService.deleteNr2jInvoice(invoice.id);
                                toast.success('Brouillon supprimé');
                                load();
                              } catch (error) {
                                toast.error(error?.message || 'Suppression impossible');
                              } finally {
                                setInvoiceBusy(false);
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" /> Supprimer
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid="nr2j-org-contracts">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">8. Contrats</h2>
              <Link
                to={`/nr2j/contrats/nouveau?organizationId=${id}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700"
              >
                <Plus className="h-3.5 w-3.5" /> Nouveau contrat
              </Link>
            </div>
            {contracts.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Aucun contrat pour cette société.</p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-100" data-testid="nr2j-org-contracts-list">
                {contracts.map((contract) => {
                  const v = contract.currentVersion;
                  const eff = v ? (v.effectiveStatus || effectiveContractStatus(v)) : null;
                  const cBadge = nr2jContractListBadge(eff, contract.approvalStatus);
                  return (
                    <li key={contract.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{contract.number}</p>
                        <p className="mt-0.5 text-sm text-gray-700">
                          {formatCentsAsEuros(v?.totalTtcCents)} €
                          {' · '}
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${cBadge.className}`}>
                            {cBadge.label}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {v?.expiresAt ? `Expire ${formatNr2jDate(v.expiresAt)}` : '—'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/nr2j/contrats/${contract.id}`}
                          className="text-xs text-indigo-700 hover:underline"
                        >
                          Ouvrir
                        </Link>
                        <button
                          type="button"
                          disabled={contractBusy}
                          className="text-xs text-gray-600 hover:underline"
                          onClick={async () => {
                            setContractBusy(true);
                            try {
                              await apiService.downloadNr2jContractPdf(contract.id, {
                                number: contract.number,
                                version: v?.version,
                              });
                            } catch (error) {
                              toast.error(error?.message || 'PDF indisponible');
                            } finally {
                              setContractBusy(false);
                            }
                          }}
                        >
                          PDF
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">9. Tâches</h2>
            <ul className="mt-3 divide-y divide-gray-100">
              {(org.tasks || []).map((task) => (
                <li key={task.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedTask(task)}
                    className="flex w-full items-center justify-between gap-2 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{task.title}</p>
                      <p className="text-xs text-gray-500">
                        {nr2jTaskTypeLabel(task.type)} · {formatNr2jDate(task.dueAt)} · {nr2jTaskStatusLabel(task.status)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            <form onSubmit={addTask} className="mt-4 grid gap-2 sm:grid-cols-2">
              <input required placeholder="Titre de la tâche" className="rounded-lg border border-gray-300 px-3 py-2 text-sm sm:col-span-2" value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} />
              <select className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={taskForm.type} onChange={(e) => setTaskForm((f) => ({ ...f, type: e.target.value }))}>
                {NR2J_TASK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <input type="datetime-local" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" value={taskForm.dueAt} onChange={(e) => setTaskForm((f) => ({ ...f, dueAt: e.target.value }))} />
              <textarea placeholder="Notes (optionnel)" rows={2} className="rounded-lg border border-gray-300 px-3 py-2 text-sm sm:col-span-2" value={taskForm.notes} onChange={(e) => setTaskForm((f) => ({ ...f, notes: e.target.value }))} />
              <button type="submit" className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white sm:col-span-2">
                <Plus className="h-4 w-4" /> Créer la tâche
              </button>
            </form>
          </div>
        </section>

        <aside className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              9. Boutiques Clickorico liées
            </h2>
            <ul className="mt-3 space-y-2">
              {(org.commercantLinks || []).map((link) => {
                const c = link.commercant || {};
                return (
                  <li
                    key={link.commercantId}
                    className="rounded-lg border border-gray-100 px-3 py-2 text-sm"
                  >
                    <p className="font-medium text-gray-900">{c.nom || link.commercantId}</p>
                    <p className="text-xs text-gray-500">
                      {[c.actif === false ? 'Inactif' : 'Actif', c.ville, c.slug].filter(Boolean).join(' · ') || '—'}
                    </p>
                    <p className="text-xs text-gray-400">Liée le {formatNr2jDate(link.createdAt)}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Link
                        to={`/commercants/${link.commercantId}`}
                        className="text-xs font-medium text-indigo-700 hover:underline"
                      >
                        Ouvrir la fiche commerçant
                      </Link>
                      {c.slug ? (
                        <a
                          href={buildCommercantPublicUrl(c)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-gray-600 hover:underline"
                        >
                          Voir la boutique
                        </a>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => unlinkShop(link.commercantId)}
                        className="inline-flex items-center gap-1 text-xs text-rose-700"
                      >
                        <Unlink className="h-3.5 w-3.5" /> Délier
                      </button>
                    </div>
                  </li>
                );
              })}
              {(org.commercantLinks || []).length === 0 ? (
                <li className="text-sm text-gray-500">Aucune boutique Clickorico liée</li>
              ) : null}
            </ul>
            <form onSubmit={linkShop} className="mt-4 space-y-2">
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={linkCommercantId}
                onChange={(e) => setLinkCommercantId(e.target.value)}
              >
                <option value="">Lier une boutique existante…</option>
                {linkableShops.map((c) => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={!linkCommercantId}
                className="inline-flex w-full items-center justify-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium disabled:opacity-50"
              >
                <Link2 className="h-4 w-4" /> Lier une boutique existante
              </button>
            </form>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">10. Historique</h2>
            <ul className="mt-3 space-y-3">
              {activities.length === 0 ? (
                <li className="text-sm text-gray-500">Aucun événement.</li>
              ) : (
                activities.map((ev) => (
                  <li key={ev.id} className="text-sm">
                    <p className="text-gray-900">{ev.message}</p>
                    <p className="text-xs text-gray-500">{formatNr2jDate(ev.createdAt)}</p>
                  </li>
                ))
              )}
            </ul>
          </div>
        </aside>
      </div>

      {convertOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/30" aria-label="Fermer" onClick={() => setConvertOpen(false)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Convertir en client NR2J</h3>
            {convertMissing.length > 0 ? (
              <>
                <p className="mt-2 text-sm text-gray-600">Impossible de convertir. Informations manquantes :</p>
                <ul className="mt-3 space-y-1 text-sm">
                  {checklistKeys.map((key) => {
                    const ok = !convertMissing.includes(key);
                    return (
                      <li key={key} className={ok ? 'text-emerald-700' : 'text-rose-700'}>
                        {ok ? '✓' : '✗'} {MISSING_LABELS[key] || key}
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    setLocalHighlightMissing(convertMissing);
                    setConvertOpen(false);
                  }}
                  className="mt-4 w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
                  data-testid="nr2j-org-complete-fiche"
                >
                  Compléter la fiche
                </button>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-gray-600">
                  Confirmer la conversion de « {organizationDisplayName(org)} » en client NR2J ?
                  La fiche quittera le pipeline actif.
                </p>
                <div className="mt-4 flex gap-2">
                  <button type="button" disabled={saving} onClick={tryConvert} className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white">
                    Confirmer la conversion
                  </button>
                  <button type="button" onClick={() => setConvertOpen(false)} className="rounded-lg border px-3 py-2 text-sm">
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {selectedTask ? (
        <Nr2jTaskDrawer
          task={{ ...selectedTask, organization: org, organizationId: id }}
          busy={taskBusy}
          onClose={() => setSelectedTask(null)}
          onSave={async (patch) => {
            setTaskBusy(true);
            try {
              await apiService.updateNr2jTask(selectedTask.id, patch);
              toast.success('Tâche mise à jour');
              setSelectedTask(null);
              load();
            } catch (error) {
              toast.error(error?.message || 'Échec');
            } finally {
              setTaskBusy(false);
            }
          }}
          onComplete={async () => {
            setTaskBusy(true);
            try {
              await apiService.completeNr2jTask(selectedTask.id);
              toast.success('Tâche terminée');
              setSelectedTask(null);
              load();
            } catch (error) {
              toast.error(error?.message || 'Échec');
            } finally {
              setTaskBusy(false);
            }
          }}
          onReopen={async () => {
            setTaskBusy(true);
            try {
              await apiService.updateNr2jTask(selectedTask.id, { status: 'OPEN' });
              toast.success('Tâche remise à faire');
              setSelectedTask(null);
              load();
            } catch (error) {
              toast.error(error?.message || 'Échec');
            } finally {
              setTaskBusy(false);
            }
          }}
          onCancel={async () => {
            if (!window.confirm('Annuler cette tâche ?')) return;
            setTaskBusy(true);
            try {
              await apiService.updateNr2jTask(selectedTask.id, { status: 'CANCELLED' });
              toast.success('Tâche annulée');
              setSelectedTask(null);
              load();
            } catch (error) {
              toast.error(error?.message || 'Échec');
            } finally {
              setTaskBusy(false);
            }
          }}
          onPlanFollowUp={() => {
            setSelectedTask(null);
            setFollowUpConflict(null);
            setFollowUpOpen(true);
          }}
        />
      ) : null}

      <Nr2jPlanFollowUpModal
        open={followUpOpen}
        busy={followUpBusy}
        organizationName={organizationDisplayName(org)}
        existingConflict={followUpConflict}
        onClose={() => {
          setFollowUpOpen(false);
          setFollowUpConflict(null);
        }}
        onSubmit={async (payload) => {
          setFollowUpBusy(true);
          try {
            await apiService.planNr2jFollowUp(id, payload);
            toast.success('Relance planifiée');
            setFollowUpOpen(false);
            setFollowUpConflict(null);
            load();
          } catch (error) {
            const existingTask = error?.data?.error?.existingTask;
            if (existingTask || error?.data?.error?.code === 'FOLLOWUP_EXISTS') {
              setFollowUpConflict({ message: error.message, existingTask });
            } else {
              toast.error(error?.message || 'Échec');
            }
          } finally {
            setFollowUpBusy(false);
          }
        }}
        onUpdateExisting={async (payload) => {
          setFollowUpBusy(true);
          try {
            await apiService.planNr2jFollowUp(id, { ...payload, updateExisting: true });
            toast.success('Relance modifiée');
            setFollowUpOpen(false);
            setFollowUpConflict(null);
            load();
          } catch (error) {
            toast.error(error?.message || 'Échec');
          } finally {
            setFollowUpBusy(false);
          }
        }}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDeleteOrg}
        tone="danger"
        title="Supprimer définitivement ce prospect ?"
        description="Cette action est irréversible. La société et ses données de prospection seront définitivement supprimées."
        confirmLabel="Supprimer définitivement"
        cancelLabel="Annuler"
        confirmDisabled={saving}
      />

      <ConfirmDialog
        isOpen={Boolean(contactToDelete)}
        onCancel={() => setContactToDelete(null)}
        onConfirm={confirmDeleteContact}
        tone="danger"
        title="Supprimer ce contact ?"
        description={
          contactToDelete?.isPrimary
            ? `« ${contactDisplayName(contactToDelete) || 'Contact'} » est le contact principal. La suppression sera refusée s’il n’existe pas d’autre contact principal.`
            : `Supprimer définitivement « ${contactDisplayName(contactToDelete) || 'ce contact'} » ?`
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmDisabled={contactDeleteBusy}
      />
    </div>
  );
}
